/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'

import { useMobxStore } from '../../../state'
import { EventCard } from './ConfirmProfile'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import colors from '@sportninja/common/constants/appColors'
import { EventDetails } from './Login'
import Icon from '../../../components/Icon'
import { useI18n } from '@shopify/react-i18n'
import { toJS } from 'mobx'

const DropdownIndicator = ({ onClick, ...props }) => {
  return (
    <div
      onClick={onClick}
      css={css`
        width: 30px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      `}
      {...props.innerProps}
    >
      <Icon
        name='chevron-down'
        color='#898a8a'
        hoverColor={colors.SOFT_STEEL}
      />
    </div>
  )
}

const TeamControl = ({ onClick, ...props }) => {
  const value = props.hasValue
    ? props.getValue()[0]
    : { name: 'Select...', image: null }

  return (
    <div
      onClick={onClick}
      css={css`
        flex: 1;
        display: flex;
        align-items: center;
        padding: 8px;
      `}
    >
      {value?.image?.full_path && (
        <img
          src={value?.image?.full_path}
          css={css`
            width: 24px;
            height: 24px;
            margin-right: 8px;
          `}
        />
      )}
      <span
        css={css`
          font-size: 16px;
          opacity: ${value.name === 'Select...' ? 0.5 : 1};
        `}
      >
        {value.name}
      </span>
    </div>
  )
}

const TeamOptions = ({ onClick, innerRef, innerProps, data }) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={() => {
        onClick()
        innerProps.onClick()
      }}
      css={css`
        display: flex;
        align-items: center;
        padding: 12px 8px;
        cursor: pointer;
        background-color: transparent;
        transition: all 0.2s ease-in-out;

        &:hover {
          background-color: white;
        }
      `}
    >
      {data?.image?.full_path && (
        <img
          src={data?.image?.full_path}
          css={css`
            width: 24px;
            height: 24px;
          `}
        />
      )}
      <span
        css={css`
          font-size: 16px;
          margin-left: 8px;
        `}
      >
        {data.name}
      </span>
    </div>
  )
}

const DivisionControl = ({ onClick, ...props }) => {
  const value = props.hasValue ? props.getValue()[0] : { name: 'Select...' }

  return (
    <div
      onClick={onClick}
      css={css`
        align-items: center;
        padding: 8px;
        flex: 1;
        display: flex;
      `}
    >
      <span
        css={css`
          font-size: 16px;
          opacity: ${value.name === 'Select...' ? 0.5 : 1};
        `}
      >
        {value.name}
      </span>
    </div>
  )
}

const DivisionOptions = ({ onClick, innerRef, innerProps, data }) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={() => {
        onClick()
        innerProps.onClick()
      }}
      css={css`
        padding: 12px 8px;
        cursor: pointer;
        background-color: transparent;
        transition: all 0.2s ease-in-out;
        display: flex;
        flex: 1;

        &:hover {
          background-color: white;
        }
      `}
    >
      <span
        css={css`
          font-size: 16px;
          margin-left: 8px;
        `}
      >
        {data.name}
      </span>
    </div>
  )
}

const Row = ({ label, value }) => (
  <div
    css={css`
      margin: 16px 0;
    `}
  >
    <span
      css={css`
        font-size: 14px;
      `}
    >
      {label}
    </span>
    <div
      css={css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 8px;
        flex: 1;
        height: 40px;
        background-color: ${colors.SOLITUDE};
        border: 1px solid ${colors.ATTENDANCE_GRAY};
        border-radius: 4px;
        margin-top: 8px;
      `}
    >
      <span
        css={css`
          font-size: 16px;
        `}
      >
        {value}
      </span>
    </div>
  </div>
)

export const NewTeamRegistration = observer(() => {
  const {
    registrationTypes: {
      state: { age_range_type, gender_type },
    },
    registrationInvite: {
      state: { eventInfo, userExtraInfo, userTeams, newTeamId },
      confirmTeamAndDivisionSelect,
      pushToStep,
    },
  } = useMobxStore()

  const [i18n] = useI18n()

  const [divisionInfo, setDivisionInfo] = useState({
    registrationId: null,
    divisionId: null,
    costPerTeam: '-',
    deposit: '-',
    age: '-',
    gender: '-',
    name: '-',
    depositRequired: '-',
    currency: {
      name: 'CAD',
    },
  })

  const [teamMenuIsOpen, setTeamMenuIsOpen] = useState(false)
  const [divisionMenuIsOpen, setDivisionMenuIsOpen] = useState(false)
  const [disabled, setDisabled] = useState(true)

  const newTeam = useMemo(
    () => userTeams?.find((team) => team.id === newTeamId),
    [newTeamId]
  )

  const [selectedTeam, setSelectedTeam] = useState(newTeam)

  useEffect(() => {
    if (selectedTeam && divisionInfo?.registrationId) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [divisionInfo, selectedTeam])

  return (
    <div>
      <button onClick={() => pushToStep(0)} css={css`
        padding-top: 16px;
        align-items: center;
        display: flex;
        gap: 8px;
      `}>
        <Icon name='arrow-left' color={colors.DEFAULT_FLAIR} />
        <p css={css`
          color: ${colors.DEFAULT_FLAIR};
          font-weight: 700;
        `}>Go Back</p>
      </button>
      <EventCard eventInfo={eventInfo} />
      <h1
        css={css`
          text-align: left;
          margin-top: 32px;
          font-size: 34px;
          font-weight: 700;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'Rift, Arial, Helvetica, sans-serif'};
        `}
      >
        NEW TEAM REGISTRATION
      </h1>
      <p
        css={css`
          font-size: 16px;
          margin: 32px 0;
        `}
      >
        TEAM REP CONTACT INFO
      </p>
      <Row
        label='Team Rep Name'
        value={userExtraInfo?.name_first + ' ' + userExtraInfo?.name_last}
      />
      <Row label='Team Rep Email Address' value={userExtraInfo?.email} />
      {userExtraInfo?.phone && (
        <Row label='Team Rep Mobile Phone' value={userExtraInfo?.phone} />
      )}

      <p
        css={css`
          font-size: 16px;
          margin: 32px 0;
        `}
      >
        TEAM INFO
      </p>

      <p
        css={css`
          font-size: 14px;
          margin-bottom: 8px;
        `}
      >
        Team Name
      </p>
      <Select
        options={userTeams ?? []}
        name='team_selector'
        defaultValue={newTeam}
        menuIsOpen={teamMenuIsOpen}
        onChange={(e) => {
          e?.id && setSelectedTeam({ ...e })
        }}
        components={{
          Option: (props) => (
            <TeamOptions
              {...props}
              onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
            />
          ),
          DropdownIndicator: (props) => (
            <DropdownIndicator
              {...props}
              onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
            />
          ),
          ValueContainer: (props) => (
            <TeamControl
              {...props}
              onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
            />
          ),
        }}
        styles={{
          container: (baseStyles) => ({
            ...baseStyles,
            width: '100%',
          }),
          control: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: colors.SOLITUDE,
            borderColor: colors.ATTENDANCE_GRAY,
            minHeight: 40,
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: colors.SOLITUDE,
            borderColor: colors.ATTENDANCE_GRAY,
          }),
          option: (baseStyles, { isFocused }) => ({
            ...baseStyles,
            backgroundColor: isFocused ? 'rgba(255, 255, 255, 0.9)' : undefined,
          }),
        }}
      />
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          margin-top: 10px;
        `}
      >
        <button
          onClick={() => pushToStep(5)}
          css={css`
            color: ${colors.DEFAULT_FLAIR};
            font-weight: 700;
            transition: all 0.2s ease-in-out;

            &:hover {
              color: ${colors.DEFAULT_FLAIR_HOVER};
            }
          `}
        >
          I want to create a new team
        </button>
      </div>

      <p
        css={css`
          font-size: 16px;
          margin: 32px 0;
        `}
      >
        COST INFO
      </p>
      <p
        css={css`
          font-size: 14px;
          margin-bottom: 8px;
        `}
      >
        Division
      </p>
      <Select
        options={eventInfo?.divisions ?? []}
        name='division_selector'
        menuIsOpen={divisionMenuIsOpen}
        onChange={(value) => {
          if (value.deposit) {
            setDivisionInfo({
              registrationId: value.id,
              divisionId: value.schedule_id,
              costPerTeam: value.team_cost,
              deposit: value.deposit,
              age: value.division_age?.id,
              gender: value.division_gender?.id,
              name: value.name,
              currency: value.currency,
              depositRequired: value.deposit_required,
            })
          }
        }}
        components={{
          Option: (props) => (
            <DivisionOptions
              {...props}
              onClick={() => setDivisionMenuIsOpen(!divisionMenuIsOpen)}
            />
          ),
          DropdownIndicator: (props) => (
            <DropdownIndicator
              {...props}
              onClick={() => setDivisionMenuIsOpen(!divisionMenuIsOpen)}
            />
          ),
          ValueContainer: (props) => (
            <DivisionControl
              {...props}
              onClick={() => setDivisionMenuIsOpen(!divisionMenuIsOpen)}
            />
          ),
        }}
        styles={{
          container: (baseStyles) => ({
            ...baseStyles,
            width: '100%',
          }),
          control: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: colors.SOLITUDE,
            borderColor: colors.ATTENDANCE_GRAY,
            minHeight: 40,
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: colors.SOLITUDE,
            borderColor: colors.ATTENDANCE_GRAY,
          }),
          option: (baseStyles, { isFocused }) => ({
            ...baseStyles,
            backgroundColor: isFocused ? 'rgba(255, 255, 255, 0.9)' : undefined,
          }),
        }}
      />
      <div
        css={css`
          margin-top: 22px;
        `}
      >
        {divisionInfo.deposit == '-' && (
          <>
            <p>Select a division to get more info *</p>
          </>
        )}
      </div>
      <div
        css={css`
          background-color: ${colors.LINK_WATER};
          padding: 16px;
          border-radius: 8px;
          margin: 12px 0 32px 0;
        `}
      >
        <EventDetails
          label='Deposit'
          value={
            divisionInfo.deposit !== '-'
              ? i18n.formatCurrency(divisionInfo?.deposit, {
                  currency: divisionInfo?.currency?.name,
                  form: 'explicit',
                })
              : '-'
          }
        />
        <EventDetails
          label='Cost Per Team'
          value={
            divisionInfo.costPerTeam !== '-'
              ? i18n.formatCurrency(divisionInfo?.costPerTeam, {
                  currency: divisionInfo?.currency?.name,
                  form: 'explicit',
                })
              : '-'
          }
        />
        <EventDetails
          label='Age'
          value={age_range_type.find((el) => el.id === divisionInfo.age)?.name}
        />
        <EventDetails
          label='Gender'
          value={gender_type.find((el) => el.id === divisionInfo.gender)?.name}
        />
      </div>

      <button
        disabled={disabled}
        onClick={() =>
          confirmTeamAndDivisionSelect({
            teamId: selectedTeam?.id,
            registrationId: divisionInfo?.registrationId,
            scheduleId: eventInfo?.id,
            info: {
              divisionId: divisionInfo?.divisionId,
              currency: divisionInfo?.currency,
              teamName: selectedTeam?.name,
              teamImageUrl: selectedTeam?.image?.full_path,
              costPerTeam: divisionInfo?.costPerTeam,
              deposit: divisionInfo?.deposit,
              divisionName: divisionInfo?.name,
              teamId: selectedTeam?.id,
              forcePayment: divisionInfo?.depositRequired,
            },
          })
        }
        css={css`
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          height: 40px;
          border-radius: 8px;
          opacity: ${disabled ? 0.5 : 1};
          background-color: ${colors.DEFAULT_FLAIR};
          color: ${colors.WHITE};
          font-size: 18px;
          font-weight: 700;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'Rift, Arial, Helvetica, sans-serif'};
          transition: all 0.2s ease-in-out;

          &:hover {
            ${!disabled &&
            `
            background-color: ${colors.DEFAULT_FLAIR_HOVER};
            `}
          }
        `}
      >
        CONFIRM MY REGISTRATION
      </button>
    </div>
  )
})
